import { designVariables } from "@studio3t/design-variables";
import {
  Icon,
  IconSVG,
  SVGColorKey,
  Tabs,
  tabButtonId,
  tabPanelId,
} from "@studio3t/generic-components";
import type { OverlayScrollbarsComponentRef } from "overlayscrollbars-react";
import { type RefObject, useEffect, useRef } from "react";
import {
  Group,
  Panel,
  type PanelImperativeHandle,
  Separator,
  useDefaultLayout,
} from "react-resizable-panels";
import styled from "styled-components";
import { EDITOR_FONT, JsonViewer } from "@/components/JsonViewer";
import { useAnyModalOpen } from "@/components/ModalChrome";
import { OverlayScroll } from "@/components/OverlayScroll";
import { ColumnSeparator, separatorLine } from "@/components/PanelSeparators";
import { userStorage } from "@/utils/userStorage";
import { InputDocumentEditor } from "./InputDocumentEditor";
import { PipelineCodeEditor } from "./PipelineCodeEditor";
import { PipelineGraphView } from "./PipelineGraphView";
import { PipelineNavBar } from "./PipelineNavBar";
import { usePipelineStaging } from "./pipelineStaging";
import { SchemaEditor } from "./SchemaEditor";
import { SchemaSeedReplaceModal } from "./SchemaVariantDropdown";
import { SourceSlotPanel } from "./SourceSlotPanel";
import { STAGE_TABS_ID_BASE, type StageTabId, StageTabs } from "./StageTabs";
import { useStudioPipeline } from "./StudioPipelineProvider";
import { StudioToolbar } from "./StudioToolbar";
import type { ScriptRunOutput, TestRunView } from "./scriptRun";
import type { StudioView } from "./stagingModel";
import {
  splitsTestRun,
  TestRunActualOutput,
  TestRunResult,
} from "./TestRunResult";
import { TestsEditorPanel } from "./TestsEditorPanel";
import { TransformScriptEditor } from "./TransformScriptEditor";

// Figma: studio workbench — the toolbar (14260-114330) across the top, then
// pipeline structure (13984-142438) and editor (13984-146903) side by side,
// with the Output/Pipeline bar (13984-142443) spanning both underneath. Mock
// widths 360/492 of 852 give the 42/58 split. Resizing via
// react-resizable-panels.

// Collapse follows the state directly; re-opening only resizes when the
// panel actually starts from collapsed (an already-open panel keeps
// whatever size the user dragged it to).
function syncOutputPanelCollapse(
  panel: PanelImperativeHandle,
  outputCollapsed: boolean,
) {
  if (outputCollapsed) panel.collapse();
  else if (panel.isCollapsed()) panel.resize("25%");
}

// A group's panel split, remembered per user under the group id
// (TTTL-186). Every layout commit is saved — the Output panel's imperative
// collapse included, so the stored split and the stored collapsed state
// never disagree on a reload.
function usePersistedLayout(id: string) {
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id,
    storage: userStorage,
  });
  return { id, defaultLayout, onLayoutChanged };
}

// The output panel stays mounted (v4 can't re-admit a conditionally
// mounted Panel into the group layout — "Layout not found") and follows
// the collapse state imperatively; each run request re-sizes it to half
// the workbench.
function useOutputPanelSizing(outputCollapsed: boolean, halfRequest: number) {
  const outputPanelRef = useRef<PanelImperativeHandle>(null);
  useEffect(() => {
    const panel = outputPanelRef.current;
    if (panel) syncOutputPanelCollapse(panel, outputCollapsed);
  }, [outputCollapsed]);
  useEffect(() => {
    if (halfRequest === 0) return;
    outputPanelRef.current?.resize("50%");
  }, [halfRequest]);
  return outputPanelRef;
}

// The scrollable viewport behind an OverlayScroll pane, once its
// instance exists (there is none while the pane is unmounted).
function paneViewport(ref: RefObject<OverlayScrollbarsComponentRef | null>) {
  return ref.current?.osInstance()?.elements().viewport;
}

// Every fresh result comes in scrolled to the TOP — without this the
// viewport keeps wherever the previous output was scrolled to.
function useScrollPanesToTop(
  refs: RefObject<OverlayScrollbarsComponentRef | null>[],
  trigger: unknown,
) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger is an intentional trigger — each fresh run resets the panes to the top
  useEffect(() => {
    for (const ref of refs) {
      const viewport = paneViewport(ref);
      if (viewport) viewport.scrollTop = 0;
    }
  }, [trigger]);
}

// A single-case test run's split face: test data (verdict + failure
// cards) on the left, the actual output document on the right, the
// column separator between them draggable like the workbench's own.
// Each side scrolls on its own and comes back to the TOP on a fresh run.
function TestRunSplit({ view }: { view: TestRunView & { actual: string } }) {
  const modalOpen = useAnyModalOpen();
  const leftRef = useRef<OverlayScrollbarsComponentRef>(null);
  const rightRef = useRef<OverlayScrollbarsComponentRef>(null);
  useScrollPanesToTop([leftRef, rightRef], view);
  return (
    <SplitBox data-testid="studio-output-result">
      <FillGroup orientation="horizontal" disabled={modalOpen}>
        <Panel defaultSize="50%" minSize="15%">
          <PaneScroll ref={leftRef}>
            <TestRunResult view={view} />
          </PaneScroll>
        </Panel>
        <ColumnSeparator />
        <Panel minSize="15%">
          <PaneScroll ref={rightRef}>
            <TestRunActualOutput actual={view.actual} />
          </PaneScroll>
        </Panel>
      </FillGroup>
    </SplitBox>
  );
}

// The non-split output's body — the test-run and script-run faces from
// OutputResult's rundown below.
function OutputResultBody({ output }: { output: ScriptRunOutput }) {
  if (output.testRun) return <TestRunResult view={output.testRun} />;
  if (output.success) {
    return (
      <RawOutput>
        <JsonViewer value={output.text} />
      </RawOutput>
    );
  }
  return (
    <OutputErrorViewer data-error>
      <JsonViewer value={output.text} />
    </OutputErrorViewer>
  );
}

// The two-pane face's view, when the run is the single case with an
// actual document that earns it; null keeps the plain scrolling body.
function splitFaceView(output: ScriptRunOutput) {
  const { testRun } = output;
  return testRun && splitsTestRun(testRun) ? testRun : null;
}

// The Output panel's content: a test run's friendly results view (a
// single-case run splits into the two-pane face above), the latest
// script run in the Raw-configuration read-only viewer (red for
// failures), or the /studio empty-state invitation before the first run.
function OutputResult({ output }: { output: ScriptRunOutput | null }) {
  const scrollRef = useRef<OverlayScrollbarsComponentRef>(null);
  useScrollPanesToTop([scrollRef], output);

  if (!output) {
    return (
      <OutputEmpty data-testid="studio-output-empty">
        Run a transform to see output
      </OutputEmpty>
    );
  }
  const splitView = splitFaceView(output);
  if (splitView) {
    return <TestRunSplit view={splitView} />;
  }
  return (
    <OutputScroll ref={scrollRef}>
      {/* OverlayScroll drops unknown props — the testid rides an inner
          wrapper. */}
      <div data-testid="studio-output-result">
        <OutputResultBody output={output} />
      </div>
    </OutputScroll>
  );
}

// The view-only studio shows a tab with nothing staged as one message
// instead of the empty structure/editor split.
function useViewOnlyEmptyMessage(studioView: StudioView, stageTab: StageTabId) {
  const { sources, transforms, sinks, tests } = usePipelineStaging();
  const { editsLocked } = useStudioPipeline();
  if (!editsLocked || studioView !== "stages") return null;
  return viewOnlyEmptyMessage(stageTab, { sources, transforms, sinks, tests });
}

// The full pipeline (Figma 14419-264437): code editor over the graph strip,
// no structure panel. Figma splits 499/200 ≈ 71/29; the divider is the
// borderlight hairline above the strip.
function WorkbenchFullView({ modalOpen }: { modalOpen: boolean }) {
  const layout = usePersistedLayout("studio-full");
  return (
    <FillGroup orientation="vertical" disabled={modalOpen} {...layout}>
      {/* biome-ignore lint/correctness/useUniqueElementIds: react-resizable-panels layout ids — deliberately stable, they key the remembered split */}
      <Panel id="studio-full-editor" defaultSize="71%" minSize="25%">
        <PanelBox data-testid="studio-editor-panel">
          <PipelineCodeEditor />
        </PanelBox>
      </Panel>
      <GraphSeparator />
      {/* biome-ignore lint/correctness/useUniqueElementIds: react-resizable-panels layout id — deliberately stable */}
      <Panel id="studio-full-graph" minSize="15%">
        <PanelBox data-testid="studio-graph-panel">
          <PipelineGraphView />
        </PanelBox>
      </Panel>
    </FillGroup>
  );
}

// The stages view: structure panel beside the stage tab's editor.
function WorkbenchStagesView({
  stageTab,
  modalOpen,
}: {
  stageTab: StageTabId;
  modalOpen: boolean;
}) {
  const layout = usePersistedLayout("studio-stages");
  return (
    <FillGroup orientation="horizontal" disabled={modalOpen} {...layout}>
      {/* biome-ignore lint/correctness/useUniqueElementIds: react-resizable-panels layout ids — deliberately stable, they key the remembered split */}
      <Panel id="studio-stages-structure" defaultSize="42%" minSize="15%">
        <PanelBox
          data-testid="studio-structure-panel"
          role="tabpanel"
          id={tabPanelId(STAGE_TABS_ID_BASE, stageTab)}
          aria-labelledby={tabButtonId(STAGE_TABS_ID_BASE, stageTab)}
        >
          <PipelineNavBar visibleSection={stageTab} />
        </PanelBox>
      </Panel>
      <ColumnSeparator />
      {/* biome-ignore lint/correctness/useUniqueElementIds: react-resizable-panels layout id — deliberately stable */}
      <Panel id="studio-stages-editor" minSize="15%">
        <PanelBox data-testid="studio-editor-panel">
          <EditorSwitch stageTab={stageTab} />
        </PanelBox>
      </Panel>
    </FillGroup>
  );
}

// The workbench's main pane: full pipeline or the stages view. Separate
// components, so the group never inherits the other view's panel layout
// when the branches swap.
function WorkbenchMain({
  studioView,
  stageTab,
  modalOpen,
}: {
  studioView: StudioView;
  stageTab: StageTabId;
  modalOpen: boolean;
}) {
  const emptyMessage = useViewOnlyEmptyMessage(studioView, stageTab);
  if (emptyMessage) {
    return (
      <ViewOnlyEmptyPanel data-testid="view-only-empty-tab">
        {emptyMessage}
      </ViewOnlyEmptyPanel>
    );
  }
  if (studioView === "full") {
    return <WorkbenchFullView modalOpen={modalOpen} />;
  }
  return <WorkbenchStagesView stageTab={stageTab} modalOpen={modalOpen} />;
}

export function StudioPanels() {
  // The stage tab bar (Figma Bridge_SecondaryTabs 14425-283750) picks which
  // section the structure panel shows in the stages view. The choice lives
  // in the staging context so the explorer can adapt tree clicks to it.
  const {
    studioView,
    stageTab,
    setStageTab,
    outputCollapsed,
    setOutputCollapsed,
    outputHalfRequest,
    scriptRunOutput,
  } = usePipelineStaging();
  // The view-only studio (a running pipeline, Figma 14894-157221): the
  // workbench stands alone in the row — every corner rounds instead of
  // fusing with the side cards — its chrome loses the mainPrimary accent
  // (card border, toolbar hairline and output gutter drop to the card1
  // gray). The Output panel stays: a viewed pipeline's last run output is
  // still worth reading.
  const { editsLocked: viewOnly } = useStudioPipeline();
  // Viewing the Full pipeline has nothing to run, so the Output panel,
  // its gutter and the collapsed bar stay off there; the Stages view keeps
  // them (a viewed pipeline's last run output is still worth reading).
  const withoutOutput = viewOnly && studioView === "full";
  // The Output bar (Figma Bridge_OutputPipeline 14419-264443) starts in its
  // Collapsed variant: just the tab header at the bottom of the card; runs
  // open it at half the workbench.
  const outputPanelRef = useOutputPanelSizing(
    outputCollapsed,
    outputHalfRequest,
  );
  // The resize handles listen at the document level and hit-test by
  // coordinates, so a modal's backdrop can't block them in the DOM —
  // dragging over a separator would resize the panels BEHIND the modal.
  // Disable every group while any modal is up instead.
  const modalOpen = useAnyModalOpen();
  const workbenchLayout = usePersistedLayout("studio-workbench");

  // One stable tree for both output states: the group and the main panel
  // are always mounted, only the separator + output panel come and go.
  // Swapping the wrapper type per state would remount the whole subtree —
  // the code editor would lose cursor/scroll/undo and the lint pass would
  // restart on every toggle.
  return (
    <Workbench $viewOnly={viewOnly}>
      <SchemaSeedReplaceModal />
      <StudioToolbar />
      {studioView !== "full" && (
        <StageTabs active={stageTab} onChange={setStageTab} />
      )}
      {withoutOutput ? (
        <MainOnly>
          <WorkbenchMain
            studioView={studioView}
            stageTab={stageTab}
            modalOpen={modalOpen}
          />
        </MainOnly>
      ) : (
        <WorkbenchGroup
          orientation="vertical"
          disabled={modalOpen}
          {...workbenchLayout}
        >
          {/* biome-ignore lint/correctness/useUniqueElementIds: react-resizable-panels layout ids — deliberately stable, not DOM uniqueness hooks */}
          <Panel key="main" id="studio-main" minSize="20%">
            <WorkbenchMain
              studioView={studioView}
              stageTab={stageTab}
              modalOpen={modalOpen}
            />
          </Panel>
          <RowSeparator $viewOnly={viewOnly} />
          <OutputPanelSlot
            panelRef={outputPanelRef}
            collapsed={outputCollapsed}
            setCollapsed={setOutputCollapsed}
            output={scriptRunOutput}
          />
        </WorkbenchGroup>
      )}
      {outputCollapsed && !withoutOutput && (
        <OutputBar
          collapsed
          onToggle={() => setOutputCollapsed(false)}
          data-testid="studio-output-bar"
        />
      )}
    </Workbench>
  );
}

// The resizable Output panel itself, below the workbench's main pane.
function OutputPanelSlot({
  panelRef,
  collapsed,
  setCollapsed,
  output,
}: {
  panelRef: RefObject<PanelImperativeHandle | null>;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  output: ScriptRunOutput | null;
}) {
  return (
    // biome-ignore lint/correctness/useUniqueElementIds: react-resizable-panels layout id — deliberately stable
    <Panel
      key="output"
      id="studio-output"
      panelRef={panelRef}
      collapsible
      collapsedSize={0}
      defaultSize={0}
      minSize="10%"
      // Dragging the separator drives the collapse state too: pulling
      // the collapsed panel open mounts the content mid-drag, pushing
      // it shut brings the collapsed bar back.
      onResize={(size) => setCollapsed(size.inPixels < 1)}
    >
      {!collapsed && (
        <OutputPanelBox data-testid="studio-output-panel">
          <OutputBar collapsed={false} onToggle={() => setCollapsed(true)} />
          <OutputResult output={output} />
        </OutputPanelBox>
      )}
    </Panel>
  );
}

// The Output bar header (Figma Bridge_OutputPipeline, SecondaryTabs row):
// one active "Output" tab and the collapse chevron on the right. Collapsed,
// it carries the card's mainPrimary hairline as its own top border; expanded,
// the resizable RowSeparator above the panel draws that line.
function OutputBar({
  collapsed,
  onToggle,
  ...rest
}: {
  collapsed: boolean;
  onToggle: () => void;
  "data-testid"?: string;
}) {
  return (
    // The whole bar is a click target in BOTH states — collapsed it
    // expands, expanded it collapses — with hover feedback like the
    // Explorer / AI Helper collapsed strips. Keyboard access rides the
    // inner tab button/chevron, and each state's onToggle is idempotent
    // (it SETS the target state), so a click bubbling through an inner
    // control and the bar can't double-toggle.
    <OutputBarBox $collapsed={collapsed} onClick={onToggle} {...rest}>
      <Tabs
        idBase="studio-output"
        label="Output panel"
        tabs={[{ id: "output", label: "Output" }]}
        activeId="output"
        // Re-selecting the only tab while collapsed opens the panel.
        onChange={() => collapsed && onToggle()}
        actions={
          <ChevronButton
            type="button"
            aria-label={collapsed ? "Expand output" : "Collapse output"}
            aria-expanded={!collapsed}
            $collapsed={collapsed}
            onClick={onToggle}
          >
            <IconSVG
              icon={Icon.Chevron}
              size={12}
              svgColorKey={SVGColorKey.semanticTextBody}
            />
          </ChevronButton>
        }
      />
    </OutputBarBox>
  );
}

// True while the Rhai script editor is the tab's live slot: the transform
// tab's only slot is the script, or the selection itself is a script
// row/pill elsewhere.
function isScriptEditorActive(
  schema: boolean,
  stageTab: StageTabId,
  editorKind: "input" | "script" | "schema",
): boolean {
  return !schema && (stageTab === "transform" || editorKind === "script");
}

// The stage tab's own slot below SCHEMA: sink has none, TESTS gets its own
// editor panel, everything else is the script/input pair (input hidden on
// the transform tab, whose only slot is the script).
function StageEditorBody({
  stageTab,
  schema,
  script,
}: {
  stageTab: StageTabId;
  schema: boolean;
  script: boolean;
}) {
  if (stageTab === "sink") {
    return (
      <EditorSlot $active={!schema}>
        <EditorUnavailablePanel data-testid="sink-editor-unavailable">
          <EditorUnavailableLine>
            Editor not available for sink
          </EditorUnavailableLine>
        </EditorUnavailablePanel>
      </EditorSlot>
    );
  }
  if (stageTab === "tests") {
    return (
      <EditorSlot $active={!schema}>
        <TestsEditorPanel />
      </EditorSlot>
    );
  }
  return (
    <ScriptAndInputSlots stageTab={stageTab} schema={schema} script={script} />
  );
}

// The script/input pair every non-sink, non-tests tab carries: the input
// document editor is hidden on the transform tab, whose only slot is the
// script.
function ScriptAndInputSlots({
  stageTab,
  schema,
  script,
}: {
  stageTab: StageTabId;
  schema: boolean;
  script: boolean;
}) {
  return (
    <>
      <EditorSlot $active={script}>
        <TransformScriptEditor />
      </EditorSlot>
      {stageTab !== "transform" && (
        <EditorSlot $active={!schema && !script}>
          <SourceWithSlot $withSlot={stageTab === "source"}>
            {stageTab === "source" && <SourceSlotPanel />}
            <InputDocumentEditor />
          </SourceWithSlot>
        </EditorSlot>
      )}
    </>
  );
}

// The editor panel follows the selection: the schema editor for the
// stage-tab SCHEMA pseudo-rows (Figma 14398-200859), the Rhai script editor
// for the TRANSFORM pseudo-row / tree script previews, the input document
// editor otherwise. A sink itself has no editor — the disabled panel per
// Figma Bridge_PipelineCode, Empty variant (14397-191576). The transform
// tab is the script editor's home (Figma 14397-86550): everything
// non-schema there shows the Rhai editor.
//
// Every editor the tab can show stays mounted (StageEditorBody included);
// the switch is CSS-only. Swapping component types instead would remount
// CodeMirror, whose view is created in an effect — the panel paints one
// empty frame (a visible blink on every drop that moves the selection
// between editors) and cursor/scroll/undo state is lost. Same rule as the
// output panel's stable tree.
function EditorSwitch({ stageTab }: { stageTab: StageTabId }) {
  const { editorKind } = usePipelineStaging();
  const schema = editorKind === "schema";
  const script = isScriptEditorActive(schema, stageTab, editorKind);
  return (
    <>
      <EditorSlot $active={schema}>
        <SchemaEditor />
      </EditorSlot>
      <StageEditorBody stageTab={stageTab} schema={schema} script={script} />
    </>
  );
}

// display:none keeps the hidden editors out of layout; CodeMirror's own
// resize observer re-measures when a slot becomes visible again.
const EditorSlot = styled.div<{ $active: boolean }>`
  height: 100%;
  display: ${(p) => (p.$active ? "block" : "none")};
`;

// The source tab's input editor, with a change capture source's slot
// setting above it; the editor keeps the rest of the height.
const SourceWithSlot = styled.div<{ $withSlot: boolean }>`
  height: 100%;
  display: flex;
  flex-direction: column;

  > :last-child {
    flex: 1;
    min-height: 0;
  }
`;

const FillGroup = styled(Group)`
  width: 100%;
  height: 100%;
`;

// Figma Bridge_PipelineCode Empty (14397-191576): disabled editor panel — a
// single comment-gray mono line on semantic/disabledbackground (#fafafa; the
// token and the 5px spacingxs aren't exported by design-variables yet —
// hardcoded). Text colour is the editor palette's comment gray.
// The view-only studio's word for a tab with nothing staged (WorkbenchMain).
function viewOnlyEmptyMessage(
  tab: StageTabId,
  staged: Record<"sources" | "transforms" | "sinks" | "tests", unknown[]>,
): string | null {
  const [list, message] = {
    source: [staged.sources, "There is no source in this pipeline"],
    transform: [staged.transforms, "There are no transforms in this pipeline"],
    sink: [staged.sinks, "There are no sinks in this pipeline"],
    tests: [staged.tests, "There are no tests in this pipeline"],
  }[tab] as [unknown[], string];
  return list.length === 0 ? message : null;
}

const ViewOnlyEmptyPanel = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${designVariables.ui.semantic.spacingm}px;
  color: ${(p) => p.theme.semanticTextPlaceholder};
  font-size: 14px;
  line-height: 1.4;
`;

const EditorUnavailablePanel = styled.div`
  height: 100%;
  background: #fafafa;
  padding: 5px ${designVariables.ui.semantic.spacingm}px 0;
`;

const EditorUnavailableLine = styled.p`
  margin: 0;
  padding: 5px 0;
  font-family: ${EDITOR_FONT};
  font-size: 14px;
  line-height: 1;
  color: ${(p) => p.theme.EditorComment};
`;

// Figma Main frame (14419-264435): the workbench carries its own 1px
// mainPrimary border on all sides — square corners: in the composed screen
// it overlaps the neighbouring Explorer/AI cards' borders as one shared
// line (the Figma component only rounds in isolation). Toolbar inside at
// the top, the Output bar closing the card at the bottom.
const Workbench = styled.div<{ $viewOnly?: boolean }>`
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid
    ${(p) => (p.$viewOnly ? p.theme.cards1Border : p.theme.semanticMainPrimary)};
  border-radius: ${(p) =>
    p.$viewOnly ? `${designVariables.ui.cards.corner}px` : 0};
`;

// The main area alone, filling the card below the toolbar and tabs — the
// view-only Full pipeline, which has no Output panel.
const MainOnly = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const WorkbenchGroup = styled(Group)`
  width: 100%;
  flex: 1;
  min-height: 0;
`;

const PanelBox = styled.div`
  height: 100%;
`;

const OutputPanelBox = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.cards1Background};
`;

// The single-test split's home under the Output bar — bounds the panel
// group's height so each pane scrolls internally instead of growing the
// panel.
const SplitBox = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
`;

// One split pane's scrolling region.
const PaneScroll = styled(OverlayScroll)`
  height: 100%;
`;

// The run result's scrolling region under the Output bar.
const OutputScroll = styled(OverlayScroll)`
  flex: 1;
  min-height: 0;

  [data-overlayscrollbars-contents] {
    display: flex;
    flex-direction: column;
    min-height: 100%;
  }

  [data-testid="studio-output-result"] {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
`;

// The raw-viewer face: the wrapper chain down to the CodeMirror editor
// stretches to the full panel height (same recipe as JsonDocumentEditor's
// EditorScroll) so the viewer — and with it the right-click Copy menu —
// covers the whole space even when the output is a single line. The
// test-run view lays out on its own instead.
const RawOutput = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  > div,
  > div > div {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .cm-editor {
    flex: 1;
  }
`;

// Collapsed, the bar sits flush at the card bottom and draws the mainPrimary
// hairline itself (Figma Bridge_OutputPipeline 13970-54870: border-top
// mainprimary, the tabs row's gray hairline at the bottom — with the active
// tab's green underline painting over the gray on the same edge, which the
// shared Tabs handles).
const OutputBarBox = styled.div<{ $collapsed: boolean }>`
  flex-shrink: 0;
  background: ${(p) => p.theme.semanticAppBackground};

  /* One big toggle target in both states, hover feedback like the
     Explorer / AI Helper collapsed strips. */
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.sidebarListItemHoverBackground};
  }
`;

// Failed runs (invalid input, failed stage, transport errors) show in the
// SAME read-only viewer as results — line numbers, Copy / Copy All menu —
// with the text forced to the status red (the syntax highlighter's token
// colors are overridden wholesale; an error message isn't JSON).
const OutputErrorViewer = styled(RawOutput)`
  .cm-content,
  .cm-line,
  .cm-line span {
    color: ${(p) => p.theme.statusError};
  }
`;

// /studio's OutputPanel empty state, restyled into the V2 panel: the
// disabled-gray italic invitation under the Output bar.
const OutputEmpty = styled.div`
  padding: ${designVariables.ui.semantic.spacingm}px;
  color: ${(p) => p.theme.semanticTextDisabled};
  font-style: italic;
  font-size: 14px;
  line-height: 1.4;
`;

// Bare 20px chevron slot per the Figma bar (List/item/type): points up while
// the panel is collapsed (expand), down while open (collapse). Icon.Chevron's
// base orientation is right (JsonViewer's fold markers rotate it the same
// way).
const ChevronButton = styled.button<{ $collapsed: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  img,
  svg {
    transition: transform 0.15s ease;
    transform: rotate(${(p) => (p.$collapsed ? -90 : 90)}deg);
  }
`;

// Gutter between the code editor and the graph strip in the Full pipeline
// view — Figma 14419-264437 draws the strip with a borderlight top hairline.
const GraphSeparator = styled(Separator).attrs<{ $line?: string }>((p) => ({
  $line: p.theme.semanticBorderLight,
}))`
  ${separatorLine};
  height: 7px;
  margin: -3px 0;
  cursor: row-resize;
  background-size: 100% 1px;

  &::after {
    top: 50%;
    left: 0;
    height: 2px;
    width: 100%;
    transform: translateY(-50%);
  }
`;

// Gutter above the output panel — Bridge_OutputPipeline (14419-264443)
// carries a mainPrimary top hairline, drawn here while the panel is open so
// the line stays draggable.
const RowSeparator = styled(Separator).attrs<{
  $line?: string;
  $viewOnly?: boolean;
}>((p) => ({
  $line: p.$viewOnly ? p.theme.cards1Border : p.theme.semanticMainPrimary,
}))`
  ${separatorLine};
  height: 7px;
  margin: -3px 0;
  cursor: row-resize;
  background-size: 100% 1px;

  &::after {
    top: 50%;
    left: 0;
    height: 2px;
    width: 100%;
    transform: translateY(-50%);
  }

`;
